import React from "react";
import { motion } from "motion/react";
import { ArrowRight, Users, CalendarDays, TrendingUp } from "lucide-react";
import { BellIcon, ChartBarIcon } from "@/components/icons/status";
import { PayrollIcon } from "@/components/icons/domain";
import { demoMonthName, demoMonthYear, demoWeek, ofMonth } from "../demoCalendar";

const thisWeek = demoWeek(0);
const nextWeek = demoWeek(1);

interface Props {
  reducedMotion?: boolean;
}

const todoItems = [
  {
    id: "dpae",
    severity: "critical" as const,
    label: "Critique",
    title: "1 DPAE en retard",
    detail: "Risque de non-conformité : action immédiate requise.",
    cta: "Traiter",
  },
  {
    id: "leaves",
    severity: "warning" as const,
    label: "À surveiller",
    title: "2 demandes de congés en attente",
    detail: "Traiter les demandes pour mettre à jour le planning.",
    cta: "Ouvrir",
  },
  {
    id: "planning",
    severity: "warning" as const,
    label: "À surveiller",
    title: "Planning S+1 à publier",
    detail: "Publier au moins 7 jours ouvrables à l'avance (IDCC 1505).",
    cta: "Publier",
  },
  {
    id: "coverage",
    severity: "info" as const,
    label: "Info",
    title: `Semaine ${nextWeek.number} en sous-effectif`,
    detail: "2 créneaux non couverts au niveau attendu.",
    cta: "Voir la couverture",
  },
];

const severityClass = {
  critical: "bg-destructive/10 text-destructive border-destructive/25",
  warning: "bg-warning/10 text-warning border-warning/25",
  info: "bg-primary/10 text-primary border-primary/25",
};

export const DashboardScene: React.FC<Props> = ({ reducedMotion }) => {
  return (
    <div className="px-3 md:px-6 pb-6 pt-3 space-y-3">
      {/* GlassHero */}
      <div className="glass-subtle shadow-glass rounded-xl border border-border/50 px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Vue franchisé</div>
        <h1 className="text-[15px] font-bold text-foreground">Bonjour Stéphane 👋</h1>
        <div className="text-[11px] text-muted-foreground">Pilotage de Carrefour Express Centre — {demoMonthYear()}</div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {[
          { icon: Users, label: "Salariés actifs", value: "12", hint: "4 en poste aujourd'hui" },
          { icon: ChartBarIcon, label: `Couverture S${thisWeek.number}`, value: "94 %", hint: "2 créneaux à ajuster" },
          { icon: CalendarDays, label: "Congés à traiter", value: "2", hint: "demandes en attente" },
          { icon: PayrollIcon, label: `Paie ${ofMonth(demoMonthName())}`, value: "En cours", hint: "export à finaliser" },
        ].map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={reducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reducedMotion ? 0 : i * 0.08, duration: 0.35 }}
              className="bg-card border rounded-xl p-2.5 shadow-sm"
            >
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                <Icon className="w-3 h-3" />
                <span className="text-[10px] uppercase tracking-wider font-semibold">{kpi.label}</span>
              </div>
              <div className="text-[16px] font-bold text-foreground leading-tight">{kpi.value}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{kpi.hint}</div>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-3">
        {/* WeeklyPlanningStatus mock */}
        <div className="bg-card border rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <div className="text-[12px] font-bold text-foreground">Semaine en cours</div>
              <div className="text-[11px] text-muted-foreground">Sem. {thisWeek.number} · {thisWeek.label} · planning publié</div>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 border border-success/25 text-[10px] font-semibold text-success">
              Publié
            </span>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-3">
            {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => {
              const heights = [70, 85, 60, 90, 95, 80, 30];
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="w-full h-14 bg-muted/30 rounded-md relative overflow-hidden">
                    <motion.div
                      initial={reducedMotion ? false : { height: 0 }}
                      animate={{ height: `${heights[i]}%` }}
                      transition={{ delay: reducedMotion ? 0 : 0.4 + i * 0.06, duration: 0.5, ease: "easeOut" }}
                      className={`absolute bottom-0 left-0 right-0 ${i === 6 ? "bg-muted-foreground/30" : "bg-primary/70"}`}
                    />
                  </div>
                  <span className="text-[10px] font-semibold text-muted-foreground">{d}</span>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <TrendingUp className="w-3 h-3" />
              Heures planifiées : <span className="font-bold text-foreground">186h</span>
            </div>
            <button className="text-[11px] font-semibold text-primary inline-flex items-center gap-1">
              Voir le planning <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* À traiter maintenant */}
        <div className="bg-card border rounded-xl p-3 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2.5">
            <BellIcon className="w-3.5 h-3.5 text-foreground" />
            <div className="text-[12px] font-bold text-foreground">À traiter maintenant</div>
          </div>
          <div className="space-y-1.5">
            {todoItems.map((item, i) => (
              <motion.div
                key={item.id}
                initial={reducedMotion ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: reducedMotion ? 0 : 0.5 + i * 0.12, duration: 0.3 }}
                className="rounded-lg border border-border/70 bg-background/80 px-2.5 py-2"
              >
                <div className="flex items-start gap-2">
                  <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${severityClass[item.severity]}`}>
                    {item.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold text-foreground leading-tight">{item.title}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{item.detail}</div>
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-primary">
                      {item.cta} <ArrowRight className="w-2.5 h-2.5" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
